import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import ArrowLeftIcon from '~/components/MarketplaceIcons/ArrowLeftIcon';
import SearchIcon from '~/components/MarketplaceIcons/SearchIcon';
import { UI_BOTTOM_COMPONENTS } from '~/constants/variables';
import { usePriceFormatter } from '~/hooks/usePriceFormatter';
import { cartDrawerStore } from '~/pages/DubaiExpress/components/CartDrawerDE/cartDrawerStore';
import { ROUTES } from '~/pages/Root/constants';
import { cartStore } from '~/stores';
import type { Offer } from '~/stores/CategoriesStore/Offer';
import type { Product } from '~/stores/CategoriesStore/Product';

interface Props {
  currentItem: Nullable<Offer | Product> | undefined;
  count: number;
  limit: number;
  isLoading: boolean;
  notifyMeText: string;
  onBack: () => void;
  onAdd: () => void;
  onNotify: () => void;
}

const ROUND_BUTTON_CLASS =
  'flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#EDEDED] border-solid bg-white p-0 text-[#141415] transition-colors duration-[120ms] hover:border-[#141415]';

const PILL_CLASS =
  'flex h-11 flex-1 cursor-pointer items-center justify-center rounded-full border-0 p-0 text-[15px] font-semibold leading-none transition-colors duration-[120ms] [font-family:inherit]';

/**
 * Mobile bottom bar of the DubaiExpress product page: back, search and a
 * pill CTA with the price. Replaces the shared navigation bar on that page.
 * The CTA opens the cart drawer once it has put a unit in the cart, and
 * shows the quantity afterwards, opening the drawer again on tap.
 */
const ProductDEBottomBarView = ({
  currentItem,
  count,
  limit,
  isLoading,
  notifyMeText,
  onBack,
  onAdd,
  onNotify,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatPrice = usePriceFormatter();

  const handleAdd = () => {
    onAdd();
    if (currentItem && cartStore.getCartItemCountById(currentItem) > count) {
      cartDrawerStore.open(currentItem.sku);
    }
  };

  const price = currentItem
    ? formatPrice(
        currentItem.discountPrice
          ? currentItem.discountPriceFormatted
          : currentItem.priceFormatted,
      )
    : '';

  const renderCta = () => {
    if (isLoading) {
      return <div className="h-11 flex-1 rounded-full bg-[#F2F2F2]" />;
    }
    if (limit <= 0) {
      return (
        <button
          type="button"
          onClick={onNotify}
          className={cn(
            PILL_CLASS,
            'border border-[#E1E1E1] border-solid bg-white text-[#141415] hover:bg-[#F7F6F2]',
          )}
        >
          {notifyMeText}
        </button>
      );
    }
    if (count > 0) {
      return (
        <button
          type="button"
          onClick={() => cartDrawerStore.open(currentItem?.sku)}
          className={cn(
            PILL_CLASS,
            'bg-[#E94E1A] text-white hover:bg-[#C43F12]',
          )}
        >
          {t('cartDrawerDE:inCartCount', { count })}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleAdd}
        className={cn(PILL_CLASS, 'bg-[#E94E1A] text-white hover:bg-[#C43F12]')}
      >
        {t('addToCart')} · {price}
      </button>
    );
  };

  return (
    <div
      id={UI_BOTTOM_COMPONENTS.NAVBAR}
      className="fixed inset-x-0 bottom-0 z-[65] flex items-center gap-2.5 bg-[linear-gradient(to_top,#fff_0%,#fff_62%,rgba(255,255,255,.5)_82%,rgba(255,255,255,0)_100%)] px-3.5 pt-[22px] pb-[calc(14px+env(safe-area-inset-bottom))] antialiased [font-family:var(--fontFamily2)] [text-rendering:auto]"
    >
      <button
        type="button"
        aria-label={t('back')}
        onClick={onBack}
        className={ROUND_BUTTON_CLASS}
      >
        <ArrowLeftIcon className="size-5" />
      </button>
      <button
        type="button"
        aria-label={t('searchProducts')}
        onClick={() => navigate(ROUTES.search)}
        className={ROUND_BUTTON_CLASS}
      >
        <SearchIcon className="size-5" />
      </button>
      {renderCta()}
    </div>
  );
};

export const ProductDEBottomBar = observer(ProductDEBottomBarView);
